/**
 * Os onze cadastros que sustentam o conteúdo, descritos em vez de codificados.
 *
 * São o resto do menu Conteúdo do sistema antigo: produto, assunto, marca,
 * veículo, versão, motor, origem, sistema, tipo de informação, tipo e categoria
 * de veículo. Todos têm o mesmo CRUD na API — listar, obter, criar, alterar,
 * excluir —, então o que muda de um para outro são os **campos**, e isso é dado.
 *
 * Sem isso seriam onze telas quase iguais. Com isso, acrescentar o décimo
 * segundo cadastro é acrescentar um item nesta lista. O último a entrar foi
 * **sistema**, o maior deles — 1.663 registros, 1.463 em uso no conteúdo — e ele
 * custou um campo novo (`porBase`) porque sua chave é composta.
 *
 * **Falta um só dos doze do sistema antigo: Fotos**, que depende do servidor de
 * arquivos e continua travado.
 *
 * **Todos são dado global.** Renomear um assunto renomeia em todos os projetos
 * e em todo o conteúdo publicado; a trava recusa a escrita em desenvolvimento e
 * a tela avisa antes.
 */

export type TipoDeCampo = "texto" | "numero" | "booleano" | "referencia"

export type Campo = {
  chave: string
  rotulo: string
  tipo: TipoDeCampo
  /** Só em `referencia`: de qual cadastro vêm as opções. */
  referencia?: "marcas" | "veiculos" | "modelos"
  obrigatorio?: boolean
  /** Aparece na grade. Sem isso o campo só existe no formulário. */
  naGrade?: boolean
  largura?: number
  ajuda?: string
}

export type Esquema = {
  chave: string
  rota: string
  /** No singular, para as frases da tela. */
  singular: string
  /**
   * Para "este/esta" nas frases. Não dá para derivar da palavra: "sistema"
   * termina em -a e é masculino, "origem" termina em -em e é feminino. Quem erra
   * isso escreve "usando este marca" na confirmação de uma exclusão — que é onde
   * a pessoa mais precisa ler sem tropeçar.
   */
  feminino?: boolean
  plural: string
  /** O que este cadastro é, em uma linha. */
  oQueE: string
  campos: Campo[]
  /** Quantos registros havia na medição — a tela diz quando corta a lista. */
  medido: number
  /**
   * O registro de id 0 é lixo do cadastro antigo na maioria — mas em produto ele
   * é "Caminhões", um produto de verdade. Filtrar ali esconderia um produto que
   * existe, e isso já custou uma correção.
   */
  zeroEhReal?: boolean
  /**
   * A chave inclui o idioma: `(id, base_id)`, 1 = português, 2 = espanhol.
   *
   * Não é enfeite — **o mesmo id existe nas duas bases**, com nomes diferentes:
   * 75 ids de sistema estão nas duas. Sem isto a tela listaria só o português,
   * e alterar um id compartilhado pareceria mexer no outro sem mexer.
   */
  porBase?: boolean
  ressalva?: string
}

/** "este" ou "esta", conforme o cadastro. Ver o campo `feminino` no tipo. */
export function esteEsta(e: Esquema) {
  return e.feminino ? "esta" : "este"
}

const nome = (rotulo = "Nome"): Campo =>
  ({ chave: "nome", rotulo, tipo: "texto", obrigatorio: true, naGrade: true })

const marketing: Campo[] = [
  { chave: "nomeMkt", rotulo: "Nome de marketing", tipo: "texto",
    ajuda: "Como aparece no site do cliente, quando difere do nome técnico." },
  { chave: "url", rotulo: "URL", tipo: "texto" },
]

export const ESQUEMAS: Esquema[] = [
  {
    chave: "produtos",
    rota: "api/produtos",
    singular: "produto",
    plural: "Produtos",
    oQueE: "O pacote em que o conteúdo é vendido. Todo conteúdo pertence a um.",
    medido: 123,
    zeroEhReal: true,
    ressalva:
      "O produto de id 0 é “Caminhões” — um produto de verdade, não registro vazio. " +
      "Ele aparece na lista de propósito.",
    campos: [
      nome(),
      { chave: "codigo", rotulo: "Código", tipo: "numero", naGrade: true, largura: 100 },
      { chave: "tipo", rotulo: "Tipo", tipo: "numero", naGrade: true, largura: 90 },
      { chave: "exibicao", rotulo: "Exibição", tipo: "numero", naGrade: true, largura: 100 },
    ],
  },
  {
    chave: "assuntos",
    rota: "api/assuntos",
    singular: "assunto",
    plural: "Assuntos",
    oQueE: "O que o conteúdo ensina: diagrama elétrico, torque, procedimento de troca.",
    medido: 43,
    porBase: true,
    campos: [
      nome(),
      { chave: "descricao", rotulo: "Descrição", tipo: "texto", naGrade: true },
      ...marketing,
    ],
  },
  {
    chave: "marcas",
    feminino: true,
    rota: "api/marcas",
    singular: "marca",
    plural: "Marcas",
    oQueE: "A montadora. É dela que o veículo herda a identificação.",
    medido: 73,
    campos: [
      nome(),
      { chave: "descricao", rotulo: "Descrição", tipo: "texto", naGrade: true },
      { chave: "fipe", rotulo: "Código FIPE", tipo: "texto", naGrade: true, largura: 120 },
      ...marketing,
    ],
  },
  {
    chave: "veiculos",
    rota: "api/veiculos",
    singular: "veículo",
    plural: "Veículos",
    oQueE: "O modelo do veículo, sempre debaixo de uma marca.",
    medido: 1202,
    campos: [
      { chave: "modelo", rotulo: "Modelo", tipo: "texto", obrigatorio: true, naGrade: true },
      { chave: "marcaId", rotulo: "Marca", tipo: "referencia", referencia: "marcas",
        obrigatorio: true, naGrade: true, largura: 170 },
      { chave: "detalhes", rotulo: "Detalhes", tipo: "texto", naGrade: true,
        ajuda: "Em boa parte dos registros repete o modelo — a lista não mostra a repetição." },
      { chave: "fipe", rotulo: "Código FIPE", tipo: "texto", largura: 120 },
      { chave: "anoIni", rotulo: "Ano inicial", tipo: "numero", naGrade: true, largura: 100 },
      { chave: "anoFim", rotulo: "Ano final", tipo: "numero", naGrade: true, largura: 100 },
      ...marketing,
    ],
  },
  {
    chave: "versoes",
    feminino: true,
    rota: "api/versoes",
    singular: "versão",
    plural: "Versões",
    oQueE: "A versão comercial de um modelo, com motorização e faixa de anos.",
    medido: 868,
    campos: [
      nome("Versão"),
      { chave: "veiculoId", rotulo: "Veículo", tipo: "referencia", referencia: "veiculos",
        obrigatorio: true, naGrade: true, largura: 200 },
      { chave: "modeloId", rotulo: "Modelo", tipo: "numero", naGrade: true, largura: 100,
        ajuda: "É o modelo do módulo de pinout, ligado por id." },
    ],
  },
  {
    chave: "motores",
    rota: "api/motores",
    singular: "motor",
    plural: "Motores",
    oQueE: "O motor, sob uma marca. É por ele que o conteúdo distingue versões.",
    medido: 1395,
    campos: [
      nome(),
      { chave: "marcaId", rotulo: "Marca", tipo: "referencia", referencia: "marcas",
        obrigatorio: true, naGrade: true, largura: 170 },
      { chave: "cilindrada", rotulo: "Cilindrada", tipo: "texto", naGrade: true, largura: 100 },
      { chave: "combustivel", rotulo: "Combustível", tipo: "texto", naGrade: true, largura: 120 },
      { chave: "potencia", rotulo: "Potência", tipo: "numero", largura: 100 },
      { chave: "quantVal", rotulo: "Válvulas", tipo: "texto", largura: 100 },
      { chave: "detalhes", rotulo: "Detalhes", tipo: "texto" },
      { chave: "perfil", rotulo: "Perfil", tipo: "texto" },
      { chave: "nomeTecnico", rotulo: "Nome técnico", tipo: "texto" },
      { chave: "aspiracao", rotulo: "Aspiração", tipo: "texto" },
      { chave: "tipoInjecao", rotulo: "Tipo de injeção", tipo: "texto" },
      { chave: "fabricante", rotulo: "Fabricante", tipo: "texto" },
      ...marketing,
    ],
  },
  {
    chave: "origens",
    feminino: true,
    rota: "api/origens",
    singular: "origem",
    plural: "Origens",
    oQueE: "De onde veio a informação — quem a produziu ou licenciou.",
    medido: 5,
    campos: [
      { chave: "descricao", rotulo: "Descrição", tipo: "texto", obrigatorio: true, naGrade: true },
      { chave: "logo", rotulo: "Logo", tipo: "texto",
        ajuda: "Nome do arquivo no servidor antigo. A tela não sobe imagem." },
      { chave: "ico", rotulo: "Ícone", tipo: "texto" },
    ],
  },
  {
    chave: "sistemas",
    rota: "api/sistemas",
    singular: "sistema",
    plural: "Sistemas",
    oQueE:
      "O sistema embarcado a que o conteúdo se refere: ABS, injeção, ar-condicionado.",
    medido: 1663,
    porBase: true,
    ressalva:
      "O maior cadastro do conteúdo, e o que mais aparece na busca: 1.463 dos 1.663 " +
      "estão em uso. Renomear um muda o nome em todo o acervo que o cita.",
    campos: [
      nome(),
      { chave: "descricao", rotulo: "Descrição", tipo: "texto", naGrade: true,
        ajuda: "Na quase totalidade dos registros repete o nome." },
      ...marketing,
    ],
  },
  {
    chave: "tipo-informacoes",
    rota: "api/tipo-informacoes",
    singular: "tipo de informação",
    plural: "Tipos de informação",
    oQueE: "Agrupa assuntos por natureza: elétrica, mecânica.",
    medido: 3,
    campos: [
      { chave: "titulo", rotulo: "Título", tipo: "texto", obrigatorio: true, naGrade: true },
    ],
  },
  {
    chave: "tipo-veiculos",
    rota: "api/tipo-veiculos",
    singular: "tipo de veículo",
    plural: "Tipos de veículo",
    oQueE: "A carroceria: hatch, sedã, picape.",
    medido: 4,
    campos: [
      { chave: "tipo", rotulo: "Tipo", tipo: "texto", obrigatorio: true, naGrade: true },
    ],
  },
  {
    chave: "categoria-veiculos",
    feminino: true,
    rota: "api/categoria-veiculos",
    singular: "categoria de veículo",
    plural: "Categorias de veículo",
    oQueE: "Linha leve, linha pesada. Vários relatórios cruzam por aqui.",
    medido: 4,
    campos: [
      { chave: "categoria", rotulo: "Categoria", tipo: "texto", obrigatorio: true, naGrade: true },
      { chave: "buscaAberta", rotulo: "Busca aberta", tipo: "booleano", naGrade: true, largura: 120,
        ajuda: "Marcado, a categoria aparece na busca pública do site." },
    ],
  },
]

export function esquemaDe(chave: string): Esquema {
  return ESQUEMAS.find((e) => e.chave === chave) ?? ESQUEMAS[0]!
}
